/**
 * Sorting and pagination arithmetic for the admin tables, kept as pure functions.
 *
 * The prototype did all of this inline in an Alpine getter. Pulling it out is not
 * ceremony: sort direction toggling and page clamping are where off-by-one bugs live,
 * and the real console will move both to the server (ADR-005 requires server-side
 * pagination once the tables are backed by Postgres). When that happens the component
 * keeps its props and only the caller of these functions changes.
 */

export type SortDirection = 'asc' | 'desc';

export interface SortState<Key extends string = string> {
  key: Key;
  direction: SortDirection;
}

/**
 * Clicking the active column flips it; clicking any other column selects it ascending.
 * Matches the prototype's `sortBy()`.
 */
export function nextSortState<Key extends string>(
  current: SortState<Key>,
  key: Key,
): SortState<Key> {
  if (current.key !== key) return { key, direction: 'asc' };
  return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

/**
 * Sort a copy of `rows` by a value accessor.
 *
 * Strings compare through an `Intl.Collator` for the active locale, because Arabic does
 * not sort correctly under a code-point comparison — `أحمد` and `احمد` differ by a hamza
 * that a naive `<` treats as a large gap. `numeric: true` also keeps `10` after `9` when
 * a column mixes digits into text. Nullish values sort last in both directions, so an
 * incomplete profile never squats at the top of the list.
 */
export function sortRows<Row>(
  rows: readonly Row[],
  value: (row: Row) => string | number | null | undefined,
  direction: SortDirection,
  locale = 'ar',
): Row[] {
  const collator = new Intl.Collator(locale, { numeric: true, sensitivity: 'base' });
  const sign = direction === 'asc' ? 1 : -1;

  return [...rows].sort((a, b) => {
    const left = value(a);
    const right = value(b);

    if (left === null || left === undefined) return right === null || right === undefined ? 0 : 1;
    if (right === null || right === undefined) return -1;

    if (typeof left === 'number' && typeof right === 'number') return (left - right) * sign;
    return collator.compare(String(left), String(right)) * sign;
  });
}

export function pageCount(total: number, perPage: number): number {
  return Math.max(1, Math.ceil(total / Math.max(1, perPage)));
}

/** Clamp a page into range — a filter change can strand the user past the last page. */
export function clampPage(page: number, total: number, perPage: number): number {
  return Math.min(Math.max(1, page), pageCount(total, perPage));
}

export function paginate<Row>(rows: readonly Row[], page: number, perPage: number): Row[] {
  const safePage = clampPage(page, rows.length, perPage);
  const start = (safePage - 1) * perPage;
  return rows.slice(start, start + perPage);
}

/**
 * The page numbers to render, windowed around the current page.
 *
 * The prototype rendered one button per page, which is fine for twelve demo rows and
 * produces a wrapping wall of buttons at a thousand talents (C8). The window keeps the
 * control a fixed width; the first and last page stay reachable through the arrows.
 */
export function pageWindow(current: number, total: number, size = 7): number[] {
  if (total <= size) return Array.from({ length: total }, (_, index) => index + 1);

  const half = Math.floor(size / 2);
  const start = Math.min(Math.max(1, current - half), total - size + 1);
  return Array.from({ length: size }, (_, index) => start + index);
}
